import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../App";
import api from "../services/api";
import "../styles/Header.css";

/* ─── Constants ─────────────────────────────────────────────── */
const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1618220179428-22790b461013?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";

const LOCAL_IMAGES = {
  "cotton lungi": "/assets/lung.png",
  "silk lungi": "/assets/lung.png",
  "premium lungi": "/assets/premium-lungi.jpg",
  "cotton bedsheet": "/assets/cotton-bedsheet.jpg",
  "satin bedsheet": "/assets/satin-bedsheet.jpg",
  default: "/assets/default-category.jpg",
};

const CATEGORY_HERO_IMAGES = {
  Lungies: "/assets/lung.png",
  Bedsheets: "/assets/bedsh.png",
  Kaavi: "/assets/kaav.png",
  Towel: "/assets/towe.png",
};

const STATIC_LINKS = [
  { to: "/about", label: "Our Story" },
  { to: "/contact", label: "Contact" },
  { to: "/track", label: "Track Order" },
];

/* ─── Helpers ────────────────────────────────────────────────── */
function getImageForSubcategory(name = "") {
  const clean = name.toLowerCase().trim();
  for (const [key, val] of Object.entries(LOCAL_IMAGES)) {
    if (key !== "default" && clean.includes(key)) return val;
  }
  return LOCAL_IMAGES.default;
}

/* ─── Icons ──────────────────────────────────────────────────── */
const IconSearch = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);
const IconUser = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconBag = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);
const IconClose = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconChevron = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const IconArrow = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
const IconIG = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);
const IconFB = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);
const IconYT = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z" />
  </svg>
);

/* ─── Cart Preview ───────────────────────────────────────────── */
function CartPreview({ cart, cartTotal, onClose }) {
  if (!cart?.length)
    return (
      <div className="cp-empty">
        <div className="cp-empty-icon">
          <IconBag />
        </div>
        <p className="cp-empty-title">Your bag is empty</p>
        <p className="cp-empty-sub">Discover our handcrafted collection</p>
        <Link to="/collection" onClick={onClose} className="cp-cta">
          Shop Now
        </Link>
      </div>
    );

  return (
    <div className="cp-filled">
      <div className="cp-head">
        <span className="cp-head-title">Your Bag</span>
        <span className="cp-head-count">
          {cart.length} item{cart.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="cp-items">
        {cart.slice(0, 3).map((item) => (
          <div className="cp-item" key={item.key || item._id}>
            <div className="cp-item-img-wrap">
              <img
                src={
                  item.image?.startsWith("http")
                    ? item.image
                    : `${window.__BASE_URL__ || ""}${item.image}`
                }
                alt={item.name}
                onError={(e) => {
                  e.target.src =
                    "https://placehold.co/56x70/f5f0e8/1a1a1a?text=KKB";
                }}
              />
              <span className="cp-qty-badge">{item.qty}</span>
            </div>
            <div className="cp-item-info">
              <p className="cp-item-name">{item.name}</p>
              <p className="cp-item-size">Size — {item.size}</p>
              <p className="cp-item-price">
                ₹{(item.price * item.qty).toLocaleString()}
              </p>
            </div>
          </div>
        ))}
        {cart.length > 3 && (
          <p className="cp-more">
            +{cart.length - 3} more item{cart.length - 3 !== 1 ? "s" : ""}
          </p>
        )}
      </div>
      <div className="cp-foot">
        <div className="cp-total-row">
          <span>Subtotal</span>
          <strong>₹{cartTotal?.toLocaleString()}</strong>
        </div>
        <Link to="/cart" onClick={onClose} className="cp-checkout-btn">
          Bag & Checkout <IconArrow />
        </Link>
        <p className="cp-shipping-note">Free shipping on orders above ₹1,000</p>
      </div>
    </div>
  );
}

/* ─── Search Suggestions ─────────────────────────────────────── */
function SearchSuggestions({ query, onSelect }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await api.products.getAll({ search: query, limit: 5 });
        setSuggestions(data.products || []);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => clearTimeout(timer);
  }, [query]);

  if (!query || query.length < 2) return null;

  return (
    <div className="ss-panel">
      {loading ? (
        <div className="ss-loading">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="ss-skeleton" />
          ))}
        </div>
      ) : suggestions.length > 0 ? (
        <>
          <p className="ss-label">Products</p>
          {suggestions.map((p) => (
            <button key={p._id} className="ss-item" onClick={() => onSelect(p)}>
              <div className="ss-img">
                <img
                  src={
                    p.images?.[0]?.startsWith("http")
                      ? p.images[0]
                      : `${window.__BASE_URL__ || ""}${p.images?.[0] || p.image}`
                  }
                  alt={p.name}
                  onError={(e) => {
                    e.target.src =
                      "https://placehold.co/44x54/f5f0e8/1a1a1a?text=KKB";
                  }}
                />
              </div>
              <div className="ss-text">
                <span className="ss-name">{p.name}</span>
                <span className="ss-price">₹{p.price?.toLocaleString()}</span>
              </div>
              <span className="ss-arrow">
                <IconArrow />
              </span>
            </button>
          ))}
          <button
            className="ss-all"
            onClick={() => onSelect({ _isSearch: true })}
          >
            View all results for &ldquo;{query}&rdquo;
          </button>
        </>
      ) : (
        <div className="ss-empty">
          <p>
            No results for <em>&ldquo;{query}&rdquo;</em>
          </p>
          <span>Try a different keyword</span>
        </div>
      )}
    </div>
  );
}

/* ─── Skeleton ───────────────────────────────────────────────── */
function HeaderSkeleton() {
  return (
    <div className="h-skeleton">
      <div className="h-skeleton-top" />
      <div className="h-skeleton-nav" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN HEADER
═══════════════════════════════════════════════════════════════ */
export default function Header() {
  const { cartCount, cart, cartTotal, searchOpen, setSearchOpen, user } =
    useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMob, setOpenMob] = useState(null);
  const [searchVal, setSearchVal] = useState("");
  const [navItems, setNavItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMega, setActiveMega] = useState(null);
  const [activeMegaCat, setActiveMegaCat] = useState(0);
  const [cartHover, setCartHover] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [selectedNav, setSelectedNav] = useState(null);

  const leaveTimerRef = useRef(null);
  const cartTimerRef = useRef(null);
  const searchInputRef = useRef(null);

  /* scroll */
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 48);
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(
        docH > 0 ? Math.min(100, (window.scrollY / docH) * 100) : 0,
      );
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* load nav - FIXED: Process navigation items to ensure proper structure */
  useEffect(() => {
    api.categories
      .getNavigation()
      .then((r) => {
        let navigation = r.navigation || [];

        // Process navigation items to ensure they have the correct structure
        navigation = navigation.map(item => {
          // If item has sub array but no mega flag, convert sub to proper column structure
          if (item.sub && item.sub.length > 0 && !item.mega) {
            // Check if sub items are strings or objects
            const linksArray = item.sub.map(subItem => {
              if (typeof subItem === 'string') {
                return {
                  label: subItem,
                  slug: subItem.toLowerCase().replace(/\s+/g, '-'),
                  isNew: false,
                  isHot: false
                };
              }
              return subItem;
            });

            // Create proper column structure
            return {
              ...item,
              mega: true,
              cols: [{
                title: item.label,
                links: linksArray
              }]
            };
          }

          // If item has cols but links are strings, convert them
          if (item.cols && item.cols.length > 0) {
            item.cols = item.cols.map(col => ({
              ...col,
              links: (col.links || []).map(link => {
                if (typeof link === 'string') {
                  return {
                    label: link,
                    slug: link.toLowerCase().replace(/\s+/g, '-'),
                    isNew: false,
                    isHot: false
                  };
                }
                return link;
              })
            }));
          }

          return item;
        });

        setNavItems(navigation);
      })
      .catch(() => setNavItems([]))
      .finally(() => setLoading(false));
  }, []);

  console.log("Nav items:", navItems);

  /* search focus */
  useEffect(() => {
    if (searchOpen) setTimeout(() => searchInputRef.current?.focus(), 80);
    else setSearchVal("");
  }, [searchOpen]);

  /* body scroll lock */
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  /* reset on route change */
  useEffect(() => {
    setMobileOpen(false);
    setActiveMega(null);
    setActiveMegaCat(0);
    if (location.pathname !== "/collection") setSelectedNav(null);
  }, [location.pathname]);

  /* mega menu handlers */
  const handleNavEnter = useCallback((label) => {
    clearTimeout(leaveTimerRef.current);
    setActiveMega(label);
    setActiveMegaCat(0);
  }, []);

  const handleNavLeave = useCallback(() => {
    leaveTimerRef.current = setTimeout(() => {
      setActiveMega(null);
      setActiveMegaCat(0);
    }, 160);
  }, []);

  const handleMegaEnter = useCallback(() => {
    clearTimeout(leaveTimerRef.current);
  }, []);

  /* cart hover */
  const handleCartEnter = () => {
    clearTimeout(cartTimerRef.current);
    setCartHover(true);
  };
  const handleCartLeave = () => {
    cartTimerRef.current = setTimeout(() => setCartHover(false), 220);
  };

  /* search */
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchVal.trim()) return;
    navigate("/collection", { state: { search: searchVal.trim() } });
    setSearchOpen(false);
  };
  const handleSuggestionSelect = (product) => {
    if (product._isSearch)
      navigate("/collection", { state: { search: searchVal.trim() } });
    else navigate(`/product/${product._id}`, { state: { product } });
    setSearchOpen(false);
  };

  const handleNavClick = useCallback((label) => {
    setSelectedNav(label);
    setActiveMega(null);
    setActiveMegaCat(0);
    setMobileOpen(false);
    setCartHover(false);
  }, []);

  const closeAll = useCallback(() => {
    setActiveMega(null);
    setActiveMegaCat(0);
    setMobileOpen(false);
    setCartHover(false);
  }, []);

  const isStaticActive = (path) => location.pathname === path;
  const isNavItemActive = (item) =>
    location.pathname === "/collection" && selectedNav === item.label;

  const activeItem = navItems.find((i) => i.label === activeMega);

  if (loading) return <HeaderSkeleton />;

  return (
    <>
      {/* ── Scroll progress bar ── */}
      <div className="h-progress-bar" style={{ width: `${scrollProgress}%` }} />

      {/* ── Dimming overlay ── */}
      <div
        className={`h-dim ${activeMega ? "visible" : ""}`}
        onClick={() => setActiveMega(null)}
        aria-hidden="true"
      />

      {/* ════════════════════════════════════════════════════════
          TOP IDENTITY BAR
      ════════════════════════════════════════════════════════ */}
      <div className="h-identity">
        <div className="h-identity-inner">
          {/* Logo */}
          <Link
            to="/"
            className="h-logo"
            onClick={() => {
              setSelectedNav(null);
              closeAll();
            }}
          >
            <div className="h-logo-mark">
              <img src="/assets/kkblogo.png" alt="KKB" />
            </div>
            <div className="h-logo-wordmark">
              <span className="h-logo-name">KKB</span>
              <span className="h-logo-sub">Tradition through Generations</span>
            </div>
          </Link>

          {/* ── Heritage Quote Block (replaces marquee) ── */}
          <div className="h-identity-quote" aria-hidden="true">
            <span className="h-quote-rule" />
            <div className="h-quote-body">
              <blockquote className="h-quote-text">
                Since 1961
              </blockquote>
            </div>
            <span className="h-quote-rule" />
          </div>

          {/* Social pills */}
          <div className="h-identity-social">
            {[
              {
                href: "https://www.instagram.com/kkbclothing_official",
                icon: <IconIG />,
                label: "Instagram",
              },
              {
                href: "https://www.facebook.com/ClothingfromKKBstores",
                icon: <IconFB />,
                label: "Facebook",
              },
              {
                href: "https://www.youtube.com/@kkbkailiking6440",
                icon: <IconYT />,
                label: "YouTube",
              },
            ].map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                aria-label={s.label}
                className="h-social-pill"
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          STICKY NAVIGATION
      ════════════════════════════════════════════════════════ */}
      <header className={`h-nav-wrap ${scrolled ? "h-nav--scrolled" : ""}`}>
        <div className="h-nav-inner">
          <Link
            to="/"
            className="h-nav-logo"
            onClick={() => {
              setSelectedNav(null);
              closeAll();
            }}
            aria-label="Home"
          >
            <img src="/assets/kkblogo.png" alt="KKB" />
          </Link>

          {/* ── Desktop navigation ── */}
          <nav className="h-desktop-nav" aria-label="Main navigation">
            <ul className="h-nav-list">
              {navItems.map((item) => {
                const hasDrop = item.sub?.length || item.cols?.length;
                const active = isNavItemActive(item);
                const megaOpen = activeMega === item.label;
                return (
                  <li
                    key={item.label}
                    className={`h-nav-item ${megaOpen ? "h-nav-item--open" : ""}`}
                    onMouseEnter={() => hasDrop && handleNavEnter(item.label)}
                    onMouseLeave={handleNavLeave}
                  >
                    <Link
                      className={`h-nav-link ${active ? "h-nav-link--active" : ""}`}
                      to={item.path}
                      state={item.state}
                      onClick={() => handleNavClick(item.label)}
                    >
                      {item.label}
                      {hasDrop && (
                        <span
                          className={`h-nav-chevron ${megaOpen ? "h-nav-chevron--open" : ""}`}
                        >
                          <IconChevron />
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}

              {STATIC_LINKS.map((l) => (
                <li key={l.to} className="h-nav-item">
                  <Link
                    className={`h-nav-link ${isStaticActive(l.to) ? "h-nav-link--active" : ""}`}
                    to={l.to}
                    onClick={closeAll}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* ── Actions ── */}
          <div className="h-actions">
            <button
              className="h-action-btn"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <IconSearch />
            </button>
            <Link
              to="/account"
              className="h-action-btn"
              aria-label="Account"
              onClick={closeAll}
            >
              {user ? (
                <span className="h-avatar">
                  {user.name?.charAt(0)?.toUpperCase()}
                </span>
              ) : (
                <IconUser />
              )}
            </Link>
            <Link to="/collection" className="h-cta-btn" onClick={closeAll}>
              Shop Now
            </Link>
            <div
              className="h-cart-wrap"
              onMouseEnter={handleCartEnter}
              onMouseLeave={handleCartLeave}
            >
              <Link
                to="/cart"
                className="h-action-btn h-cart-btn"
                aria-label={`Cart, ${cartCount} items`}
                onClick={closeAll}
              >
                <IconBag />
                {cartCount > 0 && (
                  <span className="h-cart-badge">
                    {cartCount > 9 ? "9+" : cartCount}
                  </span>
                )}
              </Link>
              <div
                className={`h-cart-drop ${cartHover ? "h-cart-drop--open" : ""}`}
              >
                <CartPreview
                  cart={cart}
                  cartTotal={cartTotal}
                  onClose={() => setCartHover(false)}
                />
              </div>
            </div>
            <button
              className={`h-hamburger ${mobileOpen ? "h-hamburger--open" : ""}`}
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      {/* ════════════════════════════════════════════════════════
          MEGA MENU - FIXED: Only show count badge if > 0
      ════════════════════════════════════════════════════════ */}
      {activeItem && (activeItem.mega || activeItem.sub?.length > 0) && (
        <div
          className="h-mega"
          onMouseEnter={handleMegaEnter}
          onMouseLeave={handleNavLeave}
          role="region"
          aria-label={`${activeItem.label} submenu`}
        >
          <div className="h-mega-grid">
            {/* Panel A: Category Sidebar */}
            <nav className="h-mega-sidebar">
              <div className="h-mega-sidebar-head">
                <Link
                  to="/collection"
                  state={{ category: activeItem.label }}
                  onClick={() => handleNavClick(activeItem.label)}
                >
                  {activeItem.label}
                  <span className="h-sidebar-arr">→</span>
                </Link>
              </div>
              <div className="h-mega-cat-list">
                {(activeItem.mega && activeItem.cols
                  ? activeItem.cols
                  : activeItem.sub?.map((s) => ({
                    title: s.label || (Array.isArray(s) ? s[0] : s),
                    links: s.links || (Array.isArray(s) && s.length > 1 ? [{ label: s[0], slug: s[1] }] : []),
                  })) || []
                ).map((col, idx) => {
                  const linkCount = col.links?.length || 0;
                  return (
                    <button
                      key={col.title}
                      className={`h-mega-cat-btn ${activeMegaCat === idx ? "h-mega-cat-btn--sel" : ""}`}
                      onMouseEnter={() => setActiveMegaCat(idx)}
                      onClick={() => setActiveMegaCat(idx)}
                    >
                      <span>{col.title}</span>
                      {/* FIXED: Only show count badge if linkCount > 0 */}
                      {linkCount > 0 && (
                        <span className="h-cat-count">
                          {linkCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <Link
                to="/collection"
                state={{ category: activeItem.label }}
                onClick={() => handleNavClick(activeItem.label)}
                className="h-mega-see-all-btn"
              >
                View all {activeItem.label} <IconArrow />
              </Link>
            </nav>

            {/* Panel B: Links Grid */}
            <div className="h-mega-links-panel">
              {(() => {
                const cols =
                  activeItem.mega && activeItem.cols
                    ? activeItem.cols
                    : [
                      {
                        title: activeItem.label,
                        links: activeItem.sub || [],
                      },
                    ];
                const activeCol = cols[activeMegaCat] || cols[0];
                const links = activeCol?.links || [];
                const half = Math.ceil(links.length / 2);
                return (
                  <div className="h-mega-links-inner" key={activeMegaCat}>
                    <div className="h-mega-links-cols">
                      {[links.slice(0, half), links.slice(half)].map(
                        (group, colIdx) => (
                          <div key={colIdx} className="h-mega-link-col">
                            {colIdx === 0 && (
                              <h4 className="h-mega-link-col-head">
                                {activeCol.title}
                              </h4>
                            )}
                            {/* {colIdx === 1 && (
                              <h4 className="h-mega-link-col-head">&nbsp;</h4>
                            )} */}
                            {group.map((link, idx) => {
                              const label = link.label || (typeof link === 'string' ? link : link[0]);
                              const slug = link.slug || (typeof link === 'string' ? link.toLowerCase().replace(/\s+/g, '-') : link[1]);
                              return (
                                <Link
                                  key={idx}
                                  to="/collection"
                                  state={{
                                    category: activeItem.label,
                                    slug,
                                    subcategory: slug,
                                    subCategory: label,
                                  }}
                                  onClick={() =>
                                    handleNavClick(activeItem.label)
                                  }
                                  className="h-mega-item-link"
                                  style={{ "--link-idx": idx }}
                                >
                                  <span className="h-item-line" />
                                  {label}
                                  {link.isNew && (
                                    <span className="h-tag-new">New</span>
                                  )}
                                  {link.isHot && (
                                    <span className="h-tag-hot">Popular</span>
                                  )}
                                </Link>
                              );
                            })}
                          </div>
                        ),
                      )}
                    </div>
                    {cols.length > 1 && (
                      <div className="h-mega-secondary-cols">
                        {cols
                          .filter((_, i) => i !== activeMegaCat)
                          .slice(0, 2)
                          .map((otherCol) => (
                            <div
                              key={otherCol.title}
                              className="h-mega-sec-group"
                            >
                              <h5 className="h-mega-sec-head">
                                {otherCol.title}
                              </h5>
                              {(otherCol.links || [])
                                .slice(0, 4)
                                .map((link, i) => {
                                  const label = link.label || (typeof link === 'string' ? link : link[0]);
                                  const slug = link.slug || (typeof link === 'string' ? link.toLowerCase().replace(/\s+/g, '-') : link[1]);
                                  return (
                                    <Link
                                      key={i}
                                      to="/collection"
                                      state={{
                                        category: activeItem.label,
                                        slug,
                                        subCategory: label,
                                      }}
                                      onClick={() =>
                                        handleNavClick(activeItem.label)
                                      }
                                      className="h-mega-item-link h-mega-item-link--sm"
                                    >
                                      <span className="h-item-line" />
                                      {label}
                                    </Link>
                                  );
                                })}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Panel C: Visual Spotlight */}
            <div className="h-mega-visual">
              <div className="h-mega-vis-hero" key={activeItem.label}>
                <img
                  src={CATEGORY_HERO_IMAGES[activeItem.label] || DEFAULT_IMAGE}
                  alt={activeItem.label}
                  onError={(e) => {
                    e.target.src = DEFAULT_IMAGE;
                  }}
                />
                <div className="h-mega-vis-veil" />
                <div className="h-mega-vis-content">
                  <span className="h-mega-vis-eyebrow">
                    Featured Collection
                  </span>
                  <span className="h-mega-vis-title">{activeItem.label}</span>
                  <Link
                    to="/collection"
                    state={{ category: activeItem.label }}
                    onClick={() => handleNavClick(activeItem.label)}
                    className="h-mega-vis-cta"
                  >
                    Shop Collection <IconArrow />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════
          SEARCH OVERLAY
      ════════════════════════════════════════════════════════ */}
      <div
        className={`h-search-overlay ${searchOpen ? "h-search-overlay--open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && setSearchOpen(false)}
        role="dialog"
        aria-label="Search"
        aria-modal="true"
      >
        <div className="h-search-panel">
          <div className="h-search-top">
            <span className="h-search-eyebrow">What are you looking for?</span>
            <button
              className="h-search-close"
              onClick={() => setSearchOpen(false)}
              aria-label="Close search"
            >
              <IconClose />
            </button>
          </div>
          <form className="h-search-form" onSubmit={handleSearch}>
            <span className="h-search-icon">
              <IconSearch />
            </span>
            <input
              ref={searchInputRef}
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              placeholder="Search lungies, bedsheets, towels…"
              className="h-search-input"
              aria-label="Search products"
              autoComplete="off"
            />
            {searchVal && (
              <button
                type="button"
                className="h-search-clear"
                onClick={() => setSearchVal("")}
                aria-label="Clear search"
              >
                <IconClose />
              </button>
            )}
            <button
              type="submit"
              className="h-search-submit"
              aria-label="Search"
            >
              Search <IconArrow />
            </button>
          </form>
          <SearchSuggestions
            query={searchVal}
            onSelect={handleSuggestionSelect}
          />
          {!searchVal && (
            <div className="h-search-trending">
              <p className="h-search-trending-label">Popular searches</p>
              <div className="h-search-chips">
                {[
                  "Cotton Lungi",
                  "Silk Lungi",
                  "Bedsheets",
                  "Towels",
                  "Gift Sets",
                ].map((term) => (
                  <button
                    key={term}
                    className="h-search-chip"
                    onClick={() => {
                      navigate("/collection", { state: { search: term } });
                      setSearchOpen(false);
                    }}
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          MOBILE DRAWER
      ════════════════════════════════════════════════════════ */}
      <div
        className={`h-mob-backdrop ${mobileOpen ? "h-mob-backdrop--open" : ""}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      <nav
        className={`h-mob-drawer ${mobileOpen ? "h-mob-drawer--open" : ""}`}
        aria-label="Mobile navigation"
        aria-hidden={!mobileOpen}
      >
        <div className="h-mob-head">
          <Link
            to="/"
            className="h-mob-logo"
            onClick={() => {
              setSelectedNav(null);
              setMobileOpen(false);
            }}
          >
            <img src="/assets/kkblogo.png" alt="KKB & Co." />
            <span>KKB</span>
          </Link>
          <button
            className="h-mob-close-btn"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <IconClose />
          </button>
        </div>

        {user && (
          <div className="h-mob-greeting">
            <span className="h-mob-greeting-avatar">
              {user.name?.charAt(0)?.toUpperCase()}
            </span>
            <span>
              Welcome, <strong>{user.name?.split(" ")[0]}</strong>
            </span>
          </div>
        )}

        <div className="h-mob-links">
          {navItems.map((item, idx) => (
            <div
              className="h-mob-item"
              key={item.label}
              style={{ "--mob-item-index": idx }}
            >
              <button
                className={`h-mob-trigger ${openMob === item.label ? "h-mob-trigger--open" : ""} ${isNavItemActive(item) ? "h-mob-trigger--active" : ""}`}
                onClick={() =>
                  setOpenMob(openMob === item.label ? null : item.label)
                }
                aria-expanded={openMob === item.label}
              >
                <span>{item.label}</span>
                <span
                  className={`h-mob-caret ${openMob === item.label ? "h-mob-caret--open" : ""}`}
                >
                  <IconChevron />
                </span>
              </button>
              <div
                className={`h-mob-sub ${openMob === item.label ? "h-mob-sub--open" : ""}`}
              >
                {item.mega && item.cols
                  ? item.cols.map((col) => (
                    <div key={col.title}>
                      <p className="h-mob-sub-head">{col.title}</p>
                      {col.links?.map((link, idx2) => (
                        <Link
                          key={idx2}
                          to="/collection"
                          state={{
                            category: item.label,
                            slug: link.slug || (typeof link === 'string' ? link.toLowerCase().replace(/\s+/g, '-') : link[1]),
                            subCategory: link.label || (typeof link === 'string' ? link : link[0]),
                          }}
                          className="h-mob-sub-link"
                          onClick={() => {
                            handleNavClick(item.label);
                            setMobileOpen(false);
                          }}
                        >
                          {link.label || (typeof link === 'string' ? link : link[0])}
                        </Link>
                      ))}
                    </div>
                  ))
                  : item.sub?.map((link, idx2) => (
                    <Link
                      key={idx2}
                      to="/collection"
                      state={{
                        category: item.label,
                        slug: link.slug || (typeof link === 'string' ? link.toLowerCase().replace(/\s+/g, '-') : link[1]),
                        subCategory: link.label || (typeof link === 'string' ? link : link[0]),
                      }}
                      className="h-mob-sub-link"
                      onClick={() => {
                        handleNavClick(item.label);
                        setMobileOpen(false);
                      }}
                    >
                      {link.label || (typeof link === 'string' ? link : link[0])}
                    </Link>
                  ))}
              </div>
            </div>
          ))}
        </div>

        <div className="h-mob-footer">
          <div className="h-mob-footer-links">
            {STATIC_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`h-mob-footer-link ${isStaticActive(l.to) ? "h-mob-footer-link--active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/account"
              className="h-mob-footer-link"
              onClick={() => setMobileOpen(false)}
            >
              {user ? "My Account" : "Login / Register"}
            </Link>
          </div>
          <Link
            to="/collection"
            className="h-mob-shop-cta"
            onClick={() => setMobileOpen(false)}
          >
            Shop Collection <IconArrow />
          </Link>
          <div className="h-mob-socials">
            {[
              {
                href: "https://www.instagram.com/kkbclothing_official",
                icon: <IconIG />,
                label: "Instagram",
              },
              {
                href: "https://www.facebook.com/ClothingfromKKBstores",
                icon: <IconFB />,
                label: "Facebook",
              },
              {
                href: "https://www.youtube.com/@kkbkailiking6440",
                icon: <IconYT />,
                label: "YouTube",
              },
            ].map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                aria-label={s.label}
                className="h-mob-social"
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
}